/**
 * The embeddable configurator — one route, no chrome.
 *
 * Mounted at ``/embed`` OUTSIDE the ``/:lang`` tree, so it never picks
 * up ``App``'s header, footer, nav or language guard. It renders the
 * same ``Landing`` the storefront does, with ``embedded`` set — there is
 * one configurator, so the embed cannot quote a different price from the
 * shop it belongs to. Since KAN-223 it boots from its own Vite entry
 * (``embed.html`` → ``embed/main.tsx``) so the frame no longer downloads
 * the three shells' eager pages; the component itself is shared.
 *
 * Language comes from ``?lang=`` because there is no ``:lang`` segment
 * here; the host page picks it when it pastes the snippet.
 *
 * ``?preview=1`` marks the admin's own preview of this page („Kde
 * prodáváte"). It changes nothing a tenant can see — only that no
 * guest cart handle is minted, because there the frame is same-origin
 * with the admin and the handle would outlive the look.
 *
 * See ``docs/specs/spec_embed_widget.md`` §E1.
 */

import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

import { useProcess, useProcesses } from "@/api/queries/processes";
import { ensureGuestHandle } from "@/embed/guestSession";
import { announceHeight, announceReady } from "@/embed/protocol";
import i18n, { isSupportedLang } from "@/i18n";
import Landing from "@/pages/Landing";
import { apiFetch } from "@/lib/api";
import type { Process } from "@/api/types";

/**
 * Report our document height to the host whenever it changes.
 *
 * The host cannot measure across the origin boundary, so an iframe
 * that does not report stays at whatever height the loader guessed —
 * either clipping the configurator or leaving a gap under it.
 */
function useReportHeight(): React.RefObject<HTMLDivElement> {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let last = 0;
    const report = (): void => {
      // Round up: a fractional height rounds down in the host and
      // produces a 1px scrollbar that never goes away.
      const next = Math.ceil(el.getBoundingClientRect().height);
      if (next !== last) {
        last = next;
        announceHeight(next);
      }
    };

    report();
    const observer = new ResizeObserver(report);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return ref;
}

/**
 * Resolve ``?process=`` to the module the embed should scope to.
 *
 * The loader passes through whatever the tenant wrote in
 * ``data-process``, so the slug is host-page input: it may be a typo,
 * a module this tenant does not offer, or one that is not a customer
 * entry point. None of those is an error worth a screen inside somebody
 * else's page — the embed falls back to the same generic configurator
 * it renders without the attribute.
 *
 * ``settled`` distinguishes "no process asked for" from "still
 * looking": rendering the generic hero first and swapping in the
 * module title a moment later would flash the wrong headline and make
 * the host page jump twice as the height is re-reported.
 */
function useEmbedProcess(slug: string | null): {
  process: Process | undefined;
  settled: boolean;
} {
  const wanted = slug?.trim() ?? "";
  const one = useProcess(wanted, { enabled: wanted.length > 0, retry: false });
  // Same list the storefront's picker uses: what a CUSTOMER of this
  // tenant may start with. A module that only exists as a continuation
  // of another one has no configurator to put on a foreign page.
  const entries = useProcesses({ enabled: wanted.length > 0 });

  if (!wanted) return { process: undefined, settled: true };
  if (one.isPending || entries.isPending) {
    return { process: undefined, settled: false };
  }

  const found = one.data;
  const isEntry =
    !!found &&
    (entries.data ?? []).some(
      (entry) => entry.slug === found.slug || entry.name === found.name,
    );

  return {
    process: found && isEntry && found.is_available ? found : undefined,
    settled: true,
  };
}

export default function EmbedPage(): JSX.Element {
  const [params] = useSearchParams();
  const ref = useReportHeight();

  // Before any API call: inside a frame the cart cookie is
  // third-party and gets dropped, so the handle has to exist on the
  // header transport instead. Mint it synchronously during the first
  // render — a quote created before the handle exists would belong to
  // nobody.
  //
  // Except in the admin preview, which frames this page on OUR OWN
  // origin. A handle minted there lands in the admin's localStorage,
  // and ``X-Guest-Session`` outranks the cart cookie server-side — so
  // one look at the preview would quietly move that browser's own
  // storefront cart onto the header transport, for good. The preview
  // is there to be looked at; it does not need a cart.
  //
  // This write happens on somebody else's website with no consent
  // banner in the frame — deliberate, and argued in full (what is
  // written, what is not, and who owes the visitor the notice) under
  // "Embedded configurator: browser storage on a third-party site" in
  // ``docs/COMPLIANCE.md``. The tenant is told in their own admin,
  // next to the snippet.
  const isPreview = params.get("preview") === "1";
  if (!isPreview) ensureGuestHandle();

  const lang = params.get("lang");
  useEffect(() => {
    if (isSupportedLang(lang) && i18n.language !== lang) {
      void i18n.changeLanguage(lang);
    }
  }, [lang]);

  const { process, settled } = useEmbedProcess(params.get("process"));

  // Announced as soon as the document is alive, NOT once the process
  // has resolved: ``ready`` is what tells the loader the browser let us
  // render at all. Waiting for an API call would make a slow network
  // indistinguishable from a refused frame.
  useEffect(() => {
    announceReady();
  }, []);

  // First step of the funnel, and the only one the server cannot derive
  // from work it is already doing: a view is a page that rendered, and
  // only the page knows. Best-effort and unawaited — a tenant's chart
  // is not worth a millisecond of the visitor's configurator, and an
  // ad blocker eating it costs a number, not a feature.
  //
  // Not from the admin preview: the tenant looking at their own
  // configurator is not a visit to it.
  useEffect(() => {
    if (isPreview) return;
    void apiFetch("/api/v1/public/embed-view", { method: "POST" }).catch(
      () => undefined,
    );
  }, [isPreview]);

  return (
    <div ref={ref} className="bg-bg text-ink" data-testid="embed-root">
      {settled && <Landing embedded process={process} />}
    </div>
  );
}
